// Breadcrumbs, Back and the sidebar highlight in the ops console (ADR 0030, DESIGN.md "Navigation
// and breadcrumbs"), the same kit as the desktop app: every history entry carries the trail of
// pages above it; each route declares its name and natural parents (`staticData.crumb`,
// lib/crumbs.tsx); the page header draws the crumbs (Crumbs), and Esc on a page with a level
// above it, Alt+Left and the mouse's Back button go back one level.

import {
  createNavTrail,
  isBackButton,
  isBackKey,
  leaveIfAllowed,
  type TrailHistory,
  type TrailRouter,
} from "@vd/ui";
import { useEffect } from "react";

declare module "@tanstack/react-router" {
  interface HistoryState {
    vdTrail?: unknown;
    vdFresh?: true;
  }
}

/** The sidebar section a path belongs to (a report under Reports). */
export function tabOf(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

const TAB_LABEL: Record<string, string> = {
  companies: "Companies",
  reports: "Reports",
  releases: "Releases",
  logs: "Logs",
  server: "Server",
  staff: "Staff",
  audit: "Audit",
};

let bound: TrailRouter | null = null;

const kit = createNavTrail({
  tabOf,
  tabLabel: (tab) => TAB_LABEL[tab],
  router: () => bound,
});

export const { usePageInfo, useHereHref, useLevels, back, open, setOwner } = kit;

/** Wire the router once, at startup (main.tsx). Returns the unsubscribe (tests). */
export function installNavTrail(router: unknown): () => void {
  bound = router as TrailRouter;
  const off = kit.install((router as { history: TrailHistory }).history);
  return () => {
    off();
    bound = null;
  };
}

const OVERLAYS =
  "[role=dialog], [role=alertdialog], [role=menu], [role=listbox], [data-radix-popper-content-wrapper]";

/** Rich text: contenteditable on the element or a parent. */
function editable(el: HTMLElement): boolean {
  if (el.isContentEditable === true) return true;
  const host = el.closest?.("[contenteditable]");
  return Boolean(host && host.getAttribute("contenteditable") !== "false");
}

/** Focus is in a text field (a search box's combobox and rich text too): its keys are its own. */
export function typing(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  if (!el || typeof el.tagName !== "string") return false;
  if (el.tagName === "TEXTAREA" || el.tagName === "SELECT") return true;
  if (el.tagName === "INPUT") {
    const type = ((el as HTMLInputElement).type || "text").toLowerCase();
    return !["checkbox", "radio", "button", "submit", "reset", "range"].includes(type);
  }
  return editable(el) || el.getAttribute?.("role") === "combobox";
}

/**
 * Back from the page on screen: one level up its crumbs, else one step back in the ops console,
 * else up to its section. Unsaved changes ask first.
 */
export function stepBack(router: TrailRouter) {
  if (kit.back(router)) return;
  const i = (router.history.location.state as { __TSR_index?: unknown } | null)?.__TSR_index;
  if (typeof i === "number" && i > 0) void leaveIfAllowed(() => router.history.back());
  else void leaveIfAllowed(() => void router.navigate({ href: `/${tabOf(router.state.location.pathname)}` }));
}

/** A second Back within this long of the first is the same gesture. */
const BACK_GAP_MS = 300;
/** How long the mouse's Back waits to see whether the browser already went back. */
const NATIVE_BACK_WAIT_MS = 60;

const indexOf = (r: TrailRouter) =>
  (r.history.location.state as { __TSR_index?: unknown } | null)?.__TSR_index;

/**
 * Esc (on a page with a level above it), Alt+Left and the mouse's Back
 * button go back, never while a dialog, menu or popover is open, and never from a text field:
 * there Alt+Left (Option+Left on a Mac) is the field's word-left and Esc is the field's. The
 * browser's own Back is held off for the gestures, and one gesture goes back once.
 */
export function useBackGestures() {
  useEffect(() => {
    const blocked = () => document.querySelector(OVERLAYS) !== null;
    let lastBack = -Infinity;
    const goBack = () => {
      const now = performance.now();
      if (!bound || now - lastBack < BACK_GAP_MS) return;
      lastBack = now;
      stepBack(bound);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || typing(e.target)) return;
      if (isBackKey(e)) {
        e.preventDefault();
        if (!e.repeat && !blocked()) goBack();
        return;
      }
      if (e.key !== "Escape" || e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      if (blocked() || !bound) return;
      const now = performance.now();
      if (now - lastBack < BACK_GAP_MS) return;
      // Only a page with a level above it: a section's own page stays put.
      if (kit.back(bound)) lastBack = now;
    };
    let pressedAt: unknown = null;
    const onPress = (e: MouseEvent) => {
      if (!isBackButton(e)) return;
      e.preventDefault();
      pressedAt = bound ? indexOf(bound) : null;
    };
    // Should the browser go back anyway, the entry has changed by the time the console looks,
    // and it leaves it at that.
    const onRelease = (e: MouseEvent) => {
      if (!isBackButton(e)) return;
      e.preventDefault();
      const at = pressedAt ?? (bound ? indexOf(bound) : null);
      pressedAt = null;
      setTimeout(() => {
        if (!bound || indexOf(bound) !== at || blocked()) return;
        goBack();
      }, NATIVE_BACK_WAIT_MS);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPress);
    window.addEventListener("mouseup", onRelease);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPress);
      window.removeEventListener("mouseup", onRelease);
    };
  }, []);
}
